// Persistent app-level banner shown just below the top nav whenever the user
// has an in-flight Notabene Travel Rule transfer (PII submitted, awaiting
// counterparty/compliance, or already authorized and waiting for them to
// click through to Review). Click → reopens the withdrawal modal at the
// Notabene stage with the persisted token/address/amount intact.

import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { CheckCircleOutline, ErrorOutline } from "@mui/icons-material";
import { useHistory, useLocation } from "react-router-dom";
import { showNewMessage } from "@intercom/messenger-js-sdk";
import {
  setIsCryptoWithdrawalModalOpen,
  setCryptoWithdrawalStage,
  resetNotabeneState,
  useWithdrawalSlice,
} from "src/store/features/withdrawal.slice";
import { useAppDispatch } from "src/store/store";
import { useCancelActiveNotabeneTransfersMutation } from "src/store/features/api/notabene";

// localStorage key for a dismissed rejection banner, keyed by txId. A declined
// transfer is terminal, so without a per-txId dismissal flag the red banner
// would be sticky forever (and would re-appear on every refresh / re-hydration).
// Keying by txId means dismissing one rejection never suppresses a *future*
// transfer's rejection.
export const rejectionDismissedKey = (txId: string): string => `tr-rejected-dismissed:${txId}`;

// A compliance decline as persisted to the slice. The live poll dispatches the
// *raw* lowercased API status (CryptoWithdrawalNotabene `checkTransferStatus`
// does `setNotabeneStatus(status.toLowerCase())`), so the slice can hold
// `tr_declined` / `denied` as well as the canonical `rejected` — all three must
// light the red banner. `canceled`/`cancelled` are deliberately EXCLUDED: those
// come from the user's own Cancel action, not a regulatory rejection, so they
// must not show the "unable to process due to regulatory requirements" copy.
const REJECTED_STATUSES = new Set(["rejected", "tr_declined", "denied"]);
export const isRejectedStatus = (status: string | null | undefined): boolean =>
  !!status && REJECTED_STATUSES.has(status.toLowerCase());

// Pre-filled message when the user opens support from the rejection banner, so
// the Intercom conversation lands with context instead of a blank composer.
const REJECTION_SUPPORT_MESSAGE =
  "Hi, my withdrawal was rejected during the Travel Rule review and I'd like some help with it.";

// Routes whose page actually mounts <CryptoWithdrawalModal/> (the Wallet page,
// via WalletOptionsDesktop/Mobile). This banner renders in the global Layout on
// EVERY route, but the modal it re-opens does not: on any other page the
// open-modal dispatches in handleResume land in redux with no mounted modal to
// show them, and Continue silently does nothing (while Cancel — a plain API
// call + state reset — keeps working). Resume must therefore route the user to
// the Wallet page first. Keep in sync with the `component: Wallets` routes in
// src/config.tsx.
const WITHDRAWAL_MODAL_ROUTES = new Set([
  "/wallets",
  "/wallets/deposit",
  "/wallets/cryptoWithdraw",
  "/wallets/fiatWithdraw",
]);

export const TravelRuleStatusBanner = (): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { pathname } = useLocation();
  const {
    notabeneTxId,
    notabeneStatus,
    notabenePiiSubmitted,
    isCryptoWithdrawalModalOpen,
    cryptoWithdrawalStage,
    withdrawalPreparedToken,
  } = useWithdrawalSlice();
  const [cancelActive, { isLoading: isCanceling }] = useCancelActiveNotabeneTransfersMutation();
  // Drives the branded confirmation modal that replaces the browser-native
  // window.confirm() — the chrome-styled dialog looked off-brand sitting over
  // the portal (issue #3396).
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  // The banner stays mounted and merely returns null in its hidden states
  // (settled / completed / modal already on Notabene-Review / cleared state),
  // so dialog open-state would otherwise survive into a *later* in-flight
  // transfer and pop the confirm open without the user clicking Cancel. Close
  // it whenever the underlying transfer changes (or clears) so it can only ever
  // be opened by an explicit Cancel click on the current transfer.
  useEffect(() => {
    setIsCancelDialogOpen(false);
  }, [notabeneTxId]);

  // Hide when there's nothing to resume, when the modal is already open showing
  // the same flow, or when the user has already moved past Notabene to
  // Review/etc. (We still show during InputDetails of a brand-new withdrawal —
  // the banner's there to help the user *return* to the in-flight one.)
  if (!notabeneTxId || !notabenePiiSubmitted) return null;
  // `settled` is terminal — the transfer has completed and the withdrawal is
  // done, so there's nothing left for the user to continue/finish. (The
  // `cryptoWithdrawalStage === "Completed"` guard below only catches the
  // in-session modal flow; a settled transfer surfaced from hydrated/persisted
  // state would otherwise render the green "continue your withdrawal" banner.)
  if (notabeneStatus === "settled") return null;
  if (cryptoWithdrawalStage === "Completed") return null;

  // A compliance decline is terminal but the user must be told — render the red
  // rejection banner (unless they've already dismissed it for this txId). This
  // replaces the old `return null`, which silently hid the banner and left the
  // user with no in-flow signal that their withdrawal had been rejected.
  const isRejected = isRejectedStatus(notabeneStatus);
  if (isRejected) {
    const alreadyDismissed =
      typeof window !== "undefined" &&
      window.localStorage.getItem(rejectionDismissedKey(notabeneTxId)) === "1";
    if (alreadyDismissed) return null;
  } else if (
    isCryptoWithdrawalModalOpen &&
    (cryptoWithdrawalStage === "Notabene" || cryptoWithdrawalStage === "Review")
  ) {
    // Non-rejected, in-flight flow already visible in the open modal — no banner.
    return null;
  }

  // Only a still-actionable `authorized` transfer shows the green
  // "continue your withdrawal" CTA. `settled` is handled as terminal above.
  const isAuthorized = notabeneStatus === "authorized";

  const handleResume = () => {
    // No prepared token in redux → user must re-verify before any final
    // submit can succeed (token expired, was consumed, or this transfer
    // predates the prepared-token deployment). Land them on Verification
    // first; handleVerificationClick skips Notabene back to Review when
    // PII is already submitted.
    if (!withdrawalPreparedToken) {
      dispatch(setCryptoWithdrawalStage("Verification"));
    } else {
      // Same-session resume — token is still valid, jump straight back to
      // the Notabene awaiting/Continue panel as before.
      dispatch(setCryptoWithdrawalStage("Notabene"));
    }
    dispatch(setIsCryptoWithdrawalModalOpen(true));
    // The modal only exists on the Wallet page. From anywhere else (dashboard,
    // portfolio, settings…) the dispatches above are invisible — route there so
    // the modal actually opens instead of the button appearing dead. Trailing
    // slashes are normalised so "/wallets/" still counts as mounted.
    const normalizedPath = pathname.replace(/\/+$/, "") || "/";
    if (!WITHDRAWAL_MODAL_ROUTES.has(normalizedPath)) {
      history.push("/wallets");
    }
  };

  // The visible Cancel button now opens a branded confirmation modal rather
  // than firing window.confirm() directly; the actual cancel runs in
  // handleConfirmCancel once the user confirms in the modal.
  const handleCancel = () => {
    if (!notabeneTxId) return;
    setIsCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!notabeneTxId) return;
    try {
      // Cancel ALL of the user's active transfers, not just the visible
      // one. The banner only surfaces one txId at a time, so per-txId
      // cancel let stranded siblings (common after testing failures) pop
      // back up immediately after one Cancel click.
      await cancelActive().unwrap();
    } catch {
      // Even if the backend call fails (network hiccup), still clear local
      // state — the user's intent is to abandon. If anything was missed
      // the next page-load fetch will refresh authoritatively.
    }
    setIsCancelDialogOpen(false);
    dispatch(resetNotabeneState());
    // Persisted cryptoWithdrawalStage may still be "Notabene"/"Review" from
    // before the cancel. Reset it so the user's next withdrawal starts at
    // InputDetails and walks the full flow (including a fresh prepared
    // token) — otherwise reopening the modal would land on Notabene with a
    // null token and 403 the create-transfer call.
    dispatch(setCryptoWithdrawalStage("InputDetails"));
  };

  // Open the in-platform Intercom messenger with a context-filled message,
  // rather than a `mailto:` (which depends on a configured mail client and
  // loses the in-app support thread). Intercom is booted in `Layout`, the same
  // place this banner mounts, so the launcher is available here.
  const handleContactSupport = () => {
    showNewMessage(REJECTION_SUPPORT_MESSAGE);
  };

  // Dismiss the (terminal) rejection banner: record the dismissal per-txId so it
  // never re-appears on refresh/re-hydration, then clear the in-flight Notabene
  // state so the user's next withdrawal starts clean at InputDetails. Unlike
  // `handleCancel`, there's nothing to cancel server-side — the transfer is
  // already terminally declined.
  const handleDismissRejection = () => {
    if (!notabeneTxId) return;
    try {
      window.localStorage.setItem(rejectionDismissedKey(notabeneTxId), "1");
    } catch {
      // Private mode / storage disabled — dismissal won't persist across reloads,
      // but resetting state below still clears it for the rest of this session.
    }
    dispatch(resetNotabeneState());
    dispatch(setCryptoWithdrawalStage("InputDetails"));
  };

  const palette = isRejected
    ? { bg: "#FEF3F2", border: "#FECDCA", title: "#7A271A", body: "#B42318", btn: "#D92D20" }
    : isAuthorized
    ? { bg: "#ECFDF3", border: "#ABEFC6", title: "#054F31", body: "#067647", btn: "#039855" }
    : { bg: "#F9FAFB", border: "#EAECF0", title: "#344054", body: "#667085", btn: "#344054" };

  return (
    <>
      <Box
        role="status"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          py: 1.25,
          px: 3,
          backgroundColor: palette.bg,
          borderBottom: `1px solid ${palette.border}`,
          // Match the main content's left offset on md+ so the banner doesn't
          // sit underneath the persistent side drawer (220px wide). On mobile
          // the drawer overlays content so no offset is needed.
          ml: { xs: 0, md: "220px" },
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          {isRejected ? (
            <ErrorOutline sx={{ fontSize: 22, color: palette.btn }} />
          ) : isAuthorized ? (
            <CheckCircleOutline sx={{ fontSize: 22, color: palette.btn }} />
          ) : (
            <CircularProgress size={18} sx={{ color: palette.btn }} />
          )}
          <Box>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: palette.title }}>
              {isRejected
                ? "Withdrawal rejected"
                : isAuthorized
                ? "Travel rule verified — continue your withdrawal"
                : "Travel rule verification in progress"}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: palette.body }}>
              {isRejected
                ? "Unfortunately we're unable to process this transfer due to regulatory requirements. Please contact your broker or our support team for further assistance."
                : isAuthorized
                ? "Compliance has approved your transfer. Click Continue to finish your withdrawal."
                : "We're waiting on counterparty / compliance authorization. You'll be able to continue as soon as it clears."}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {isRejected ? (
            <>
              <Box
                component="button"
                onClick={handleDismissRejection}
                data-testid="tr-rejected-dismiss"
                sx={{
                  backgroundColor: "transparent",
                  color: palette.body,
                  border: `1px solid ${palette.border}`,
                  borderRadius: "999px",
                  padding: "6px 14px",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Dismiss
              </Box>
              <Box
                component="button"
                onClick={handleContactSupport}
                data-testid="tr-rejected-contact-support"
                sx={{
                  backgroundColor: palette.btn,
                  color: "#FFFFFF",
                  border: "none",
                  borderRadius: "999px",
                  padding: "6px 14px",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Contact support
              </Box>
            </>
          ) : (
            <>
              <Box
                component="button"
                onClick={handleCancel}
                disabled={isCanceling}
                sx={{
                  backgroundColor: "transparent",
                  color: palette.body,
                  border: `1px solid ${palette.border}`,
                  borderRadius: "999px",
                  padding: "6px 14px",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: isCanceling ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                  opacity: isCanceling ? 0.6 : 1,
                }}
              >
                {isCanceling ? "Canceling…" : "Cancel"}
              </Box>
              <Box
                component="button"
                onClick={handleResume}
                sx={{
                  backgroundColor: palette.btn,
                  color: "#FFFFFF",
                  border: "none",
                  borderRadius: "999px",
                  padding: "6px 14px",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {isAuthorized ? "Continue" : "View status"}
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Dialog
        open={isCancelDialogOpen && !isRejected}
        onClose={() => !isCanceling && setIsCancelDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            padding: "8px",
            maxWidth: "420px",
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "18px", fontWeight: 700, color: "#101828", pb: 1 }}>
          {isAuthorized ? "Cancel this withdrawal?" : "Cancel Travel Rule verification?"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "14px", color: "#667085" }}>
            {isAuthorized
              ? "Your withdrawal has been verified. If you cancel now you'll need to start over to send your funds."
              : "If you cancel now you'll need to start the Travel Rule verification over to continue your withdrawal."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => setIsCancelDialogOpen(false)}
            disabled={isCanceling}
            disableRipple
            sx={{
              textTransform: "none",
              backgroundColor: "transparent",
              color: "#344054",
              border: "1px solid #D0D5DD",
              borderRadius: "999px",
              padding: "8px 18px",
              fontSize: "14px",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#F9FAFB" },
            }}
          >
            Keep withdrawal
          </Button>
          <Button
            onClick={handleConfirmCancel}
            disabled={isCanceling}
            disableRipple
            sx={{
              textTransform: "none",
              backgroundColor: "#D92D20",
              color: "#FFFFFF",
              borderRadius: "999px",
              padding: "8px 18px",
              fontSize: "14px",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#B42318" },
              "&.Mui-disabled": { backgroundColor: "#D92D20", color: "#FFFFFF", opacity: 0.6 },
            }}
          >
            {isCanceling ? "Canceling…" : "Yes, cancel"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
